import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';

interface TokenUsageStats {
  totalTokens: number;
  totalCost: number;
  totalRequests: number;
  averageResponseTime: number;
  successRate: number;
  topProcesses: Array<{
    processName: string;
    totalTokens: number;
    totalCost: number;
    requestCount: number;
  }>;
  hourlyUsage: Array<{
    hour: string;
    totalTokens: number;
    totalCost: number;
    requestCount: number;
  }>;
  dailyUsage: Array<{
    date: string;
    totalTokens: number;
    totalCost: number;
    requestCount: number;
  }>;
  realTimeUsage: {
    currentHourTokens: number;
    currentHourCost: number;
    currentHourRequests: number;
    lastHourTokens: number;
    lastHourCost: number;
    lastHourRequests: number;
  };
}

const TokenUsage: React.FC = () => {
  const [stats, setStats] = useState<TokenUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  const [selectedView, setSelectedView] = useState<'overview' | 'hourly' | 'daily' | 'realtime'>('overview');

  useEffect(() => {
    fetchTokenUsage();
  }, [selectedPeriod]);

  const fetchTokenUsage = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard stats
      const dashboardResponse = await orchestratorApi.getTokenUsageDashboard(selectedPeriod);

      // Fetch real-time usage
      const realtimeResponse = await orchestratorApi.getTokenUsageRealtime(1);

      // Fetch hourly usage for the selected period
      const hourlyResponse = await orchestratorApi.getTokenUsageHourly({
        startDate: getStartDate(selectedPeriod),
        endDate: new Date().toISOString()
      });

      // Fetch daily usage for the selected period
      const dailyResponse = await orchestratorApi.getTokenUsageDaily({
        startDate: getStartDate(selectedPeriod),
        endDate: new Date().toISOString()
      });

      const dashboardData = dashboardResponse.ok ? dashboardResponse.data : null;
      const realtimeData = realtimeResponse.ok ? realtimeResponse.data : null;
      const hourlyData = hourlyResponse.ok ? hourlyResponse.data : null;
      const dailyData = dailyResponse.ok ? dailyResponse.data : null;

      console.log('API Response data:', { dashboardData, realtimeData, hourlyData, dailyData });

      if (!dashboardData) {
        throw new Error('Failed to load dashboard data');
      }

      setStats({
        totalTokens: dashboardData.totalTokens || 0,
        totalCost: dashboardData.totalCost || 0,
        totalRequests: dashboardData.totalRequests || 0,
        averageResponseTime: dashboardData.averageResponseTime || 0,
        successRate: dashboardData.successRate || 0,
        topProcesses: dashboardData.topProcesses || [],
        hourlyUsage: hourlyData || [],
        dailyUsage: dailyData || [],
        realTimeUsage: realtimeData || {
          currentHourTokens: 0,
          currentHourCost: 0,
          currentHourRequests: 0,
          lastHourTokens: 0,
          lastHourCost: 0,
          lastHourRequests: 0,
        }
      });

    } catch (err) {
      console.error('Failed to fetch token usage:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch token usage data');
    } finally {
      setLoading(false);
    }
  };

  const getStartDate = (period: string): string => {
    const now = new Date();
    const startDate = new Date(now);

    switch (period) {
      case '1h':
        startDate.setHours(now.getHours() - 1);
        break;
      case '24h':
        startDate.setDate(now.getDate() - 1);
        break;
      case '7d':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(now.getDate() - 30);
        break;
      default:
        startDate.setDate(now.getDate() - 1);
    }

    return startDate.toISOString();
  };

  const formatTokens = (tokens: number): string => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  const formatCost = (cost: number): string => {
    return `$${cost.toFixed(4)}`;
  };

  const formatResponseTime = (ms: number): string => {
    return `${ms.toFixed(0)}ms`;
  };

  const formatPercentage = (percentage: number): string => {
    return `${(percentage * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="token-usage-container">
        <div className="loading">Loading token usage data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-usage-container">
        <div className="error-message">
          <h3>Error Loading Token Usage</h3>
          <p>{error}</p>
          <button onClick={fetchTokenUsage} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stats || typeof stats !== 'object') {
    return (
      <div className="token-usage-container">
        <div className="no-data">No token usage data available</div>
      </div>
    );
  }

  return (
    <div className="token-usage-container">
      <div className="token-usage-header">
        <h2>📊 Token Usage Analytics</h2>
        <p>
          Monitor AI model token consumption, costs, and performance metrics across all services.
        </p>
      </div>

      <div className="token-usage-controls">
        <div className="period-selector">
          <label>Time Period:</label>
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>

        <div className="view-selector">
          <label>View:</label>
          <select 
            value={selectedView} 
            onChange={(e) => setSelectedView(e.target.value as any)}
          >
            <option value="overview">Overview</option>
            <option value="hourly">Hourly Breakdown</option>
            <option value="daily">Daily Breakdown</option>
            <option value="realtime">Real-time</option>
          </select>
        </div>

        <button onClick={fetchTokenUsage} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      {/* Overview Cards */}
      <div className="overview-cards">
        <div className="stat-card">
          <div className="stat-label">Total Tokens</div>
          <div className="stat-value">{formatTokens(stats?.totalTokens || 0)}</div>
          <div className="stat-period">{selectedPeriod}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Total Cost</div>
          <div className="stat-value">{formatCost(stats?.totalCost || 0)}</div>
          <div className="stat-period">{selectedPeriod}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Total Requests</div>
          <div className="stat-value">{(stats?.totalRequests || 0).toLocaleString()}</div>
          <div className="stat-period">{selectedPeriod}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Avg Response Time</div>
          <div className="stat-value">{formatResponseTime(stats?.averageResponseTime || 0)}</div>
          <div className="stat-period">{selectedPeriod}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Success Rate</div>
          <div className="stat-value">{formatPercentage(stats?.successRate || 0)}</div>
          <div className="stat-period">{selectedPeriod}</div>
        </div>
      </div>

      {/* Real-time Usage */}
      <div className="realtime-section">
        <h3>🔴 Real-time Usage (Current Hour)</h3>
        <div className="realtime-cards">
          <div className="realtime-card">
            <div className="realtime-label">Current Hour</div>
            <div className="realtime-value">{formatTokens(stats?.realTimeUsage?.currentHourTokens || 0)} tokens</div>
            <div className="realtime-sub">{stats?.realTimeUsage?.currentHourRequests || 0} requests</div>
          </div>

          <div className="realtime-card">
            <div className="realtime-label">Last Hour</div>
            <div className="realtime-value">{formatTokens(stats?.realTimeUsage?.lastHourTokens || 0)} tokens</div>
            <div className="realtime-sub">{stats?.realTimeUsage?.lastHourRequests || 0} requests</div>
          </div>

          <div className="realtime-card">
            <div className="realtime-label">Current Cost</div>
            <div className="realtime-value">{formatCost(stats?.realTimeUsage?.currentHourCost || 0)}</div>
            <div className="realtime-sub">This hour</div>
          </div>
        </div>
      </div>

      {/* Top Processes */}
      <div className="top-processes-section">
        <h3>🔝 Top Processes by Token Usage</h3>
        <div className="processes-table">
          <table>
            <thead>
              <tr>
                <th>Process Name</th>
                <th>Tokens</th>
                <th>Cost</th>
                <th>Requests</th>
                <th>Avg Tokens/Request</th>
              </tr>
            </thead>
            <tbody>
              {stats.topProcesses.map((process, index) => (
                <tr key={index}>
                  <td className="process-name">{process.processName}</td>
                  <td>{formatTokens(process.totalTokens)}</td>
                  <td>{formatCost(process.totalCost)}</td>
                  <td>{process.requestCount.toLocaleString()}</td>
                  <td>{formatTokens(Math.round(process.totalTokens / process.requestCount))}</td>
                </tr>
              ))}
              {stats.topProcesses.length === 0 && (
                <tr>
                  <td colSpan={5} className="no-data">No process data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Time Series Charts */}
      {selectedView === 'hourly' && stats.hourlyUsage.length > 0 && (
        <div className="hourly-section">
          <h3>⏰ Hourly Token Usage</h3>
          <div className="usage-chart">
            {stats.hourlyUsage.map((hour, index) => (
              <div key={index} className="chart-bar">
                <div 
                  className="bar" 
                  style={{ height: `${Math.max(10, (hour.totalTokens / Math.max(...stats.hourlyUsage.map(h => h.totalTokens))) * 200)}px` }}
                  title={`${formatTokens(hour.totalTokens)} tokens, ${formatCost(hour.totalCost)}`}
                >
                  <span className="bar-label">{formatTokens(hour.totalTokens)}</span>
                </div>
                <span className="bar-time">{hour.hour.split('T')[1]?.split(':')[0] || hour.hour}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedView === 'daily' && stats.dailyUsage.length > 0 && (
        <div className="daily-section">
          <h3>📅 Daily Token Usage</h3>
          <div className="usage-chart">
            {stats.dailyUsage.map((day, index) => (
              <div key={index} className="chart-bar">
                <div 
                  className="bar" 
                  style={{ height: `${Math.max(10, (day.totalTokens / Math.max(...stats.dailyUsage.map(d => d.totalTokens))) * 200)}px` }}
                  title={`${formatTokens(day.totalTokens)} tokens, ${formatCost(day.totalCost)}`}
                >
                  <span className="bar-label">{formatTokens(day.totalTokens)}</span>
                </div>
                <span className="bar-time">{day.date.split('T')[0]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default TokenUsage;